import { arSA, enUS } from "@mui/material/locale";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CaretDownIcon } from "@phosphor-icons/react";
import { type ComponentProps, type ReactNode, useMemo } from "react";

import { useLocaleInfo } from "@/hooks/useLocaleInfo";

export const SelectIcon = (props: ComponentProps<typeof CaretDownIcon>) => (
  <CaretDownIcon {...props} size={20} weight="bold" />
);

export const MuiThemeProvider = ({ children }: { children: ReactNode }) => {
  const { dir, locale } = useLocaleInfo();

  const theme = useMemo(
    () =>
      createTheme(
        {
          direction: dir,
          // it's not possible to use CSS variables here:
          // https://github.com/mui/material-ui/issues/12827
          palette: {
            primary: {
              main: "#003E2B", // same as --color-surface-accent-stronger
            },
            error: {
              main: "#DE370C", // same as --color-semantic-error-strong
            },
            success: {
              main: "#2e8285", // same as --color-semantic-success-strong
            },
            whatsapp: {
              main: "#40C351", // same as --color-whatsapp-green
            },
          },
          typography: {
            fontFamily: `var(--font-sans)`,
          },
          components: {
            MuiBadge: {
              styleOverrides: {
                badge: {
                  height: "14px",
                  minWidth: "14px",
                  fontWeight: "bold",
                  fontSize: "10px",
                  lineHeight: "12px",
                  padding: 0,
                },
              },
            },
            MuiLink: {
              styleOverrides: {
                root: {
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: 14,
                },
              },
            },
            MuiRadio: {
              styleOverrides: {
                root: {
                  "& .MuiSvgIcon-root": {
                    fontSize: 20,
                  },
                  "&.Mui-checked": {
                    color: "var(--color-surface-accent-stronger)",
                  },
                },
              },
            },
            MuiAvatar: {
              styleOverrides: {
                root: {
                  // arabic font is not centered properly
                  // only latin characters are expected here
                  fontFamily: "var(--font-sans-input)",
                },
              },
            },
            MuiBottomNavigation: {
              styleOverrides: {
                root: {
                  "& .MuiBottomNavigationAction-root": {
                    padding: "0 calc(var(--spacing)*2)",
                    color: "var(--color-text-secondary)",
                  },
                  "& .MuiBottomNavigationAction-label": {
                    marginTop: "2px",
                    fontWeight: "500",
                    fontSize: "12px",
                    lineHeight: "16px",

                    "&.Mui-selected": {
                      fontSize: "12px", // to prevent text enlargment on selection
                    },
                  },
                  "& .MuiBottomNavigationAction-root.Mui-selected": {
                    color: "var(--color-surface-accent-stronger)",
                    fontSize: "inherit",
                    zIndex: 2,
                    "&::before": {
                      content: "''",
                      position: "absolute",
                      inset: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      opacity: 0.4,
                      background:
                        "radial-gradient(circle at 50% 50%, #A7FDC3 40%, #FFFFFF 60%)",
                      zIndex: -1,
                    },
                  },
                },
              },
            },
            MuiSkeleton: {
              defaultProps: {
                animation: "wave",
                variant: "rounded",
              },
              styleOverrides: {
                root: {
                  backgroundColor: "var(--color-stroke-neutral-softer)",
                  variants: [
                    {
                      props: { variant: "rounded" },
                      style: {
                        borderRadius: "var(--radius-lg)",
                      },
                    },
                  ],
                },
              },
            },
            MuiChip: {
              styleOverrides: {
                root: {
                  variants: [
                    {
                      props: { color: "primary" },
                      style: {
                        backgroundColor: "var(--color-surface-accent-stronger)",
                        color: "var(--color-text-inverted)",
                      },
                    },
                    {
                      props: { color: "success" },
                      style: {
                        backgroundColor: "var(--color-core-rahal-100)",
                        color: "var(--color-core-rahal-600)",
                      },
                    },
                    {
                      props: { color: "warning" },
                      style: {
                        backgroundColor: "rgba(251, 230, 208, 0.5)",
                        color: "var(--color-amber-600)",
                      },
                    },
                    {
                      props: { color: "error" },
                      style: {
                        backgroundColor: "rgba(254, 243, 242, 1)",
                        color: "var(--color-semantic-error-strong)",
                      },
                    },
                  ],
                },
              },
            },
            MuiCheckbox: {
              styleOverrides: {
                root: {
                  "&.Mui-checked": {
                    backgroundColor: "#fff",
                    color: "var(--color-text-interactive)",
                  },
                },
              },
            },
            MuiSlider: {
              styleOverrides: {
                root: {
                  height: 6,
                },
                rail: {
                  backgroundColor: "var(--color-stroke-neutral-soft)",
                  opacity: 1,
                },
                thumb: {
                  width: 16,
                  height: 24,
                  backgroundColor: "var(--color-surface-accent-medium)",
                  borderRadius: "4px",
                  border: "7px solid var(--color-surface-accent-stronger)",
                },
              },
            },
            MuiSnackbarContent: {
              styleOverrides: {
                root: {
                  maxWidth: "var(--container-2xs)",
                  borderRadius: "var(--radius-lg)",
                  backgroundColor: "var(--color-zinc-800)",
                  boxShadow: "var(--shadow-md)",
                },
              },
            },
            MuiDialog: {
              styleOverrides: {
                paper: {
                  borderRadius: "var(--radius-2xl)",
                  padding: "calc(var(--spacing)*2)",
                },
              },
            },
            MuiAutocomplete: {
              defaultProps: {
                popupIcon: <SelectIcon />,
              },
            },
            MuiButton: {
              styleOverrides: {
                root: {
                  variants: [
                    {
                      props: { color: "primary", variant: "contained" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "var(--color-surface-pressed-accent-strong)",
                        },
                        "&:disabled, &:disabled:focus": {
                          backgroundColor:
                            "var(--color-surface-disabled-accent-strong)",
                          color: "var(--color-text-inverted)",
                        },
                      },
                    },
                    {
                      props: { color: "primary", variant: "outlined" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-surface-accent-stronger) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "primary", variant: "text" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-surface-accent-stronger) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "error", variant: "contained" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "var(--color-semantic-error-pressed-strong)",
                        },
                      },
                    },
                    {
                      props: { color: "error", variant: "outlined" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-error-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "error", variant: "text" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-error-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "success", variant: "contained" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "var(--color-semantic-success-pressed-strong)",
                        },
                      },
                    },
                    {
                      props: { color: "success", variant: "outlined" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-success-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "success", variant: "text" },
                      style: {
                        "&:focus:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-success-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "whatsapp", variant: "contained" },
                      style: {
                        color: "var(--color-text-inverted)",
                      },
                    },
                    {
                      props: { size: "small" },
                      style: {
                        fontSize: "14px",
                        lineHeight: "20px",
                        paddingTop: "calc(var(--spacing)*2)",
                        paddingBottom: "calc(var(--spacing)*2)",
                        paddingLeft: "calc(var(--spacing)*4)",
                        paddingRight: "calc(var(--spacing)*4)",
                      },
                    },
                    {
                      props: { size: "medium" },
                      style: {
                        fontSize: "16px",
                        lineHeight: "22px",
                        paddingTop: "calc(var(--spacing)*3)",
                        paddingBottom: "calc(var(--spacing)*3)",
                        paddingLeft: "calc(var(--spacing)*6)",
                        paddingRight: "calc(var(--spacing)*6)",
                      },
                    },
                    {
                      props: { size: "large" },
                      style: {
                        fontSize: "18px",
                        lineHeight: "26px",
                        fontWeight: "var(--font-weight-bold)",
                        paddingTop: "calc(var(--spacing)*3.5)",
                        paddingBottom: "calc(var(--spacing)*3.5)",
                        paddingLeft: "calc(var(--spacing)*8)",
                        paddingRight: "calc(var(--spacing)*8)",
                      },
                    },
                    {
                      props: { variant: "contained" },
                      style: {
                        "&:disabled, &:disabled:focus": {
                          backgroundColor:
                            "var(--color-surface-disabled-accent-strong)",
                          borderColor:
                            "var(--color-surface-disabled-accent-strong)",
                          color: "var(--color-text-inverted)",
                        },
                      },
                    },
                    {
                      props: { variant: "outlined" },
                      style: {
                        "&:disabled, &:disabled:focus": {
                          backgroundColor: "transparent",
                          borderColor:
                            "var(--color-surface-disabled-accent-strong)",
                          color: "var(--color-text-disabled-interactive)",
                        },
                      },
                    },
                    {
                      props: { variant: "text" },
                      style: {
                        "&:disabled, &:disabled:focus": {
                          backgroundColor: "transparent",
                          color: "var(--color-text-disabled-interactive)",
                        },
                      },
                    },
                  ],
                  textTransform: "none",
                  fontWeight: "var(--font-weight-bold)",
                  borderRadius: "999px",
                },
              },
            },
            MuiDrawer: {
              styleOverrides: {
                paper: {
                  margin: "auto",
                  maxWidth: "var(--container-xl)",
                  borderRadius: "32px 32px 0 0",
                  padding: "16px 16px var(--drawer-bottom-padding) 16px",
                  overscrollBehavior: "contain",
                },
              },
            },
            MuiSelect: {
              defaultProps: {
                variant: "outlined",
                IconComponent: SelectIcon,
              },
              styleOverrides: {
                root: {
                  // we need separate font for form inputs because of issues with noto sans arabic height
                  "& .MuiSelect-select": {
                    fontFamily: "var(--font-sans-input)",
                  },
                  ".MuiSelect-icon": {
                    insetInlineEnd: "calc(var(--spacing)*3)",
                    color: "var(--color-text-primary)",
                    transition: "transform 0.1s ease-in-out",
                    top: "unset",
                  },
                  variants: [
                    {
                      props: { variant: "outlined" },
                      style: {
                        borderRadius: "16px",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E7E9EE",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--color-surface-accent-stronger)",
                          borderWidth: "1px !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--color-surface-accent-stronger)",
                          borderWidth: "1px !important",
                        },
                      },
                    },
                  ],
                },
              },
            },
            MuiAlert: {
              styleOverrides: {
                root: {
                  border: "1px solid var(--color-stroke-neutral-soft)",
                  borderRadius: "var(--radius-2xl)",
                  variants: [
                    {
                      props: { severity: "success" },
                      style: {
                        backgroundColor: "var(--color-semantic-success-soft)",
                      },
                    },
                    {
                      props: { severity: "error" },
                      style: {
                        backgroundColor:
                          "var(--color-semantic-error-pressed-soft)",
                      },
                    },
                  ],
                },
              },
            },
            MuiTextField: {
              defaultProps: {
                variant: "outlined",
                slotProps: {
                  select: {
                    IconComponent: () => null, // no icon for TextInput select
                  },
                },
              },
              styleOverrides: {
                root: {
                  // we need separate font for form inputs because of issues with noto sans arabic height
                  "& .MuiInputBase-input": {
                    fontFamily: "var(--font-sans-input)",
                  },
                  variants: [
                    {
                      props: { variant: "outlined" },
                      style: {
                        "& .MuiInputBase-root": {
                          borderRadius: "16px",
                          backgroundColor: "var(--color-white)",

                          "&.MuiAutocomplete-inputRoot": {
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderWidth: "1px !important",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E7E9EE",
                        },
                        "& .MuiInputAdornment-root": {
                          transform: "translateY(10px)",
                        },
                      },
                    },
                    {
                      props: ({ variant, hiddenLabel }) =>
                        variant === "outlined" && !hiddenLabel,
                      style: {
                        "& .MuiOutlinedInput-notchedOutline": {
                          "legend > span": {
                            display: "none",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          marginTop: "28px",
                          marginBottom: "6px",
                          paddingTop: 0,
                          paddingBottom: 0,
                          lineHeight: 1,

                          "&.MuiInputBase-input.MuiAutocomplete-input": {
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        },
                        "& .MuiInputLabel-shrink": {
                          transform: "translate(14px, 6px) scale(0.75)",
                        },
                      },
                    },
                    {
                      props: { size: "small" },
                      style: {
                        "& .MuiOutlinedInput-input": {
                          marginTop: "10px",
                          marginBottom: "11px",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          top: "-1px",
                        },
                      },
                    },
                  ],
                  "& .MuiSelect-select": {
                    borderRadius: "16px !important",
                  },
                  "& .Mui-focused": {
                    "& .MuiInputBase-input": {
                      borderColor: "var(--color-surface-accent-stronger)",
                    },
                  },
                },
              },
            },
            MuiMobileStepper: {
              styleOverrides: {
                root: {
                  width: "100%",
                  paddingInline: 0,
                  backgroundColor: "transparent",
                  "& .MuiLinearProgress-root": {
                    width: "100%",
                    background: "#E7E9EE",
                    borderRadius: "4px",
                  },
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "var(--color-surface-accent-stronger)",
                    borderRadius: "4px",
                  },
                },
              },
            },
          },
        },
        locale === "ar" ? arSA : enUS,
      ),
    [dir, locale],
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
